import React, { useEffect, useState } from "react";
import { Layout, Dropdown, Button, Avatar, Tooltip } from "antd";
import { UserOutlined, MenuOutlined, LogoutOutlined, LaptopOutlined, AppleFilled, WindowsFilled, QuestionCircleOutlined } from "@ant-design/icons";
import styles from "./Topbar.module.scss";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { picbotLogo } from "../../assets/images";
import { useDispatch, useSelector } from "react-redux";
import { clearAuthData } from "../../redux/actions/authActions";
import { GetDesktopAppsService } from "../../apis/events";

const { Header } = Layout;

const TopBar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [appUrls, setAppUrls] = useState({ mac: null, windows: null });

  useEffect(() => {
    GetDesktopAppsService()
      .then(res => {
        if (res.data?.success) {
          setAppUrls({ mac: res.data.data.mac, windows: res.data.data.windows });
        }
      })
      .catch(() => {});
  }, []);

  const menuItems = [
    { key: "dashboard", label: "Dashboard", link: "/app/dashboard" },
    { key: "events", label: "Events", link: "/app/events" },
    ...(user?.user_type !== "sub-organizers" && user?.user_type !== "organizers"
      ? [{ key: "organizers", label: "Organizers", link: "/app/organizers" }]
      : []),
  ];

  const mobileMenuItems = [
    {
      key: "dashboard",
      label: "Dashboard",
      onClick: () => { navigate("/app/dashboard"); },
    },
    {
      key: "events",
      label: "Events",
      onClick: () => { navigate("/app/events"); },
    },
    ...(user?.user_type !== "sub-organizers" && user?.user_type !== "organizers"
      ? [{ key: "organizers", label: "Organizers", onClick: () => { navigate("/app/organizers"); } }]
      : []),
    {
      key: "logout",
      label: "Logout",
      onClick: () => { dispatch(clearAuthData()); },
    },
  ];

  const profileMenu = {
    items: [
      {
        key: "profile",
        label: "Profile",
        icon: <UserOutlined />,
      },
      {
        key: "logout",
        label: "Logout",
        icon: <LogoutOutlined />,
        onClick: () => { dispatch(clearAuthData()); },
      },
    ],
  };

  const downloadMenuItems = [
    ...(appUrls.mac ? [
      {
        key: "mac",
        label: (
          <div className="d-flex align-items-center justify-content-between gap-3" style={{ minWidth: 200 }}>
            <div className="d-flex align-items-center gap-2">
              <AppleFilled style={{ fontSize: 16 }} />
              <span>Mac App</span>
            </div>
            <Tooltip title="Setup Guide">
              <QuestionCircleOutlined
                style={{ color: '#8A8A8B', fontSize: 14 }}
                onClick={(e) => { e.stopPropagation(); navigate('/app/desktop-guide/mac'); }}
              />
            </Tooltip>
          </div>
        ),
        onClick: () => { window.open(appUrls.mac, '_blank'); },
      },
    ] : []),
    ...(appUrls.windows ? [
      {
        key: "windows",
        label: (
          <div className="d-flex align-items-center gap-2" style={{ minWidth: 200 }}>
            <WindowsFilled style={{ fontSize: 16 }} />
            <span>Windows App</span>
          </div>
        ),
        onClick: () => { window.open(appUrls.windows, '_blank'); },
      },
    ] : []),
  ];

  const hideMenu = pathname === "/app/events/create" || sessionStorage.getItem('from_auto_login') === '1';
  const autoLoginName = hideMenu ? sessionStorage.getItem('auto_login_name') || '' : '';

  return (
    <Header className={styles.topbar} style={hideMenu ? { padding: '8px 0', lineHeight: 'normal' } : {}}>
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          <div className={styles.logo}>
            <span>
              <img src={picbotLogo} alt="logo" />
            </span>
          </div>

          {!hideMenu && (
            <div className={styles.mobileMenu}>
              <Dropdown menu={{ items: mobileMenuItems }} placement="bottomRight">
                <Button
                  type="text"
                  icon={<MenuOutlined />}
                  onClick={() => setMobileMenuVisible(!mobileMenuVisible)}
                />
              </Dropdown>
            </div>
          )}

          <div className={styles.profile}>
            <div className="d-flex align-items-center gap-2">
              {downloadMenuItems.length > 0 && (
                <Dropdown menu={{ items: downloadMenuItems }} placement="bottomRight" trigger={['hover']}>
                  <Button
                    type="text"
                    icon={<LaptopOutlined style={{ fontSize: 18 }} />}
                    style={{ color: '#595C5C' }}
                  />
                </Dropdown>
              )}

              {hideMenu ? (
                <div className="d-flex align-items-center">
                  {autoLoginName && (
                    <span className={styles.autoLoginName}>{autoLoginName}</span>
                  )}
                </div>
              ) : (
                <Dropdown menu={profileMenu} placement="bottomRight">
                  <Avatar style={{ cursor: "pointer" }}>U</Avatar>
                </Dropdown>
              )}
            </div>
          </div>
        </div>
        {!hideMenu && (
          <nav className={styles.navigation}>
            {menuItems.map((item) => (
              <Link
                key={item.key}
                to={`${item.link}`}
                className={pathname.includes(item.key) ? styles.active : ""}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </Header>
  );
};

export default TopBar;
